import { z } from "zod";

/**
 * DTOs for the identity context.
 *
 * These are the contract between apps/api and apps/app. Use cases return these types; route
 * handlers pass them through unchanged; `@gladis/api-client` derives its types from the same
 * schemas. A contract change is therefore a compile error on both sides at once, rather than a
 * runtime surprise on one of them.
 */

export const RoleSchema = z.enum(["owner", "admin"]);
export type RoleValue = z.infer<typeof RoleSchema>;

/**
 * The member's profile picture, ready for an `<img src>`, or null if they have none.
 *
 * A URL AND NOT BYTES, deliberately. Today it holds a `data:` URL, because the bytes live in
 * Postgres and travel inside a response that was already authenticated — an `<img>` pointing at an
 * authenticated endpoint could not send the access token, which this application holds in memory
 * only. If the bytes ever move to object storage this becomes an `https` signed URL and not one
 * consumer changes. See specs/020-profile-pictures/research.md R2.
 *
 * `z.string()` AND NOT `z.url()`: Zod's URL validator rejects the `data:` scheme, which is the only
 * scheme currently in use. There is nothing to validate here in any case — the value is produced by
 * the server, never accepted from a client.
 */
const AvatarUrlSchema = z.string().nullable();

export const UserDtoSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  displayName: z.string().min(1).max(100),
  avatarUrl: AvatarUrlSchema,
});
export type UserDto = z.infer<typeof UserDtoSchema>;

export const HouseholdDtoSchema = z.object({
  id: z.uuid(),
  name: z.string().min(1).max(100),
  role: RoleSchema,
});
export type HouseholdDto = z.infer<typeof HouseholdDtoSchema>;

/**
 * `GET /api/me`.
 *
 * `activeHousehold` is null when the user has no active membership, which is the signal the SPA
 * uses to route to onboarding (FR-016). It is resolved server-side — the SPA never computes it, so
 * there is one rule in one place.
 *
 * That rule changed in 005 (FR-032): the active household is the user's STORED preference if it is
 * still among `households`, and otherwise the membership with the latest `joined_at`, which is 003's
 * FR-021 rule serving as the fallback. The shape did not change, so the 004 header keeps working.
 *
 * `households` is every household the caller is an active member of, and it is what the switcher
 * renders. It is computed under row-level security BEFORE the stored preference is consulted, so
 * the preference selects among rows the server already proved the caller may see — it never widens
 * the set. That ordering is why the switcher is not an authorization bypass (FR-033).
 */
export const CurrentUserDtoSchema = z.object({
  user: UserDtoSchema,
  activeHousehold: HouseholdDtoSchema.nullable(),
  households: z.array(HouseholdDtoSchema),
});
export type CurrentUserDto = z.infer<typeof CurrentUserDtoSchema>;

export const MembershipDtoSchema = z.object({
  id: z.uuid(),
  householdId: z.uuid(),
  userId: z.uuid(),
  displayName: z.string(),
  role: RoleSchema,
  joinedAt: z.iso.datetime(),
});
export type MembershipDto = z.infer<typeof MembershipDtoSchema>;

/**
 * `POST /api/households/:id/invites`.
 *
 * `inviteUrl` is ALWAYS present and `emailDelivery: "failed"` still returns 201 — a mail server
 * that is down must not block onboarding (FR-023/FR-024). The copy-link affordance is the primary
 * path, not the fallback.
 */
export const InviteDtoSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  expiresAt: z.iso.datetime(),
  inviteUrl: z.url(),
  emailDelivery: z.enum(["sent", "failed"]),
});
export type InviteDto = z.infer<typeof InviteDtoSchema>;

/** `GET /api/invites/:token` — unauthenticated, so it exposes only what the invitee will see anyway. */
export const InvitePreviewDtoSchema = z.object({
  householdName: z.string(),
  inviterDisplayName: z.string(),
  email: z.email(),
  requiresAccount: z.boolean(),
});
export type InvitePreviewDto = z.infer<typeof InvitePreviewDtoSchema>;

export const SessionDtoSchema = z.object({
  accessToken: z.string().min(1),
  expiresAt: z.iso.datetime(),
});
export type SessionDto = z.infer<typeof SessionDtoSchema>;

// --- feature 005 -----------------------------------------------------------

/**
 * `GET /api/households/:householdId/members` (FR-001).
 *
 * `isSelf` is computed server-side so the interface never compares ids to decide whether to offer
 * Remove or Leave. The two are different flows with different rules — FR-006 refuses a self-removal
 * outright — and deciding which to show is not a judgement the browser should be making.
 *
 * Active memberships only. A removed member never appears here; their name survives on the activity
 * entries they wrote, as a snapshot (FR-043).
 */
export const MemberDtoSchema = z.object({
  userId: z.uuid(),
  displayName: z.string().min(1).max(100),
  email: z.email(),
  role: RoleSchema,
  joinedAt: z.iso.datetime(),
  isSelf: z.boolean(),
  /**
   * This one field carries feature 020. Every household-scoped page already fetches the member list,
   * so a surface resolves a person to a face from data it already holds — no page pays per row, and
   * no savings, income or expenses response schema had to change to show a picture.
   */
  avatarUrl: AvatarUrlSchema,
});
export type MemberDto = z.infer<typeof MemberDtoSchema>;

export const MemberListDtoSchema = z.object({
  members: z.array(MemberDtoSchema),
});
export type MemberListDto = z.infer<typeof MemberListDtoSchema>;

/**
 * `GET /api/households/:householdId/invites` (FR-028).
 *
 * Expired invites are LISTED and marked, not hidden — the product tells the inviter to resend one,
 * which they cannot do if they cannot see it. Accepted and revoked invites are excluded; an accepted
 * one shows up as a member instead.
 */
export const PendingInviteDtoSchema = z.object({
  id: z.uuid(),
  email: z.email(),
  /**
   * Who sent it, or `null` when the household no longer knows — feature 021.
   *
   * `invited_by` is `on delete set null`, so an inviter who deletes their account leaves this
   * unresolvable. It used to arrive as the English "A former member", composed in the use case where
   * no catalogue can reach; the edge renders the stand-in now.
   */
  invitedByDisplayName: z.string().nullable(),
  createdAt: z.iso.datetime(),
  expiresAt: z.iso.datetime(),
  isExpired: z.boolean(),
});
export type PendingInviteDto = z.infer<typeof PendingInviteDtoSchema>;

export const PendingInviteListDtoSchema = z.object({
  invites: z.array(PendingInviteDtoSchema),
});
export type PendingInviteListDto = z.infer<typeof PendingInviteListDtoSchema>;

/** `POST /api/invites/:id/resend` (FR-029). A new token and a new 7-day expiry; the old link dies. */
export const ResendInviteDtoSchema = z.object({
  inviteUrl: z.url(),
  expiresAt: z.iso.datetime(),
  emailDelivery: z.enum(["sent", "failed"]),
});
export type ResendInviteDto = z.infer<typeof ResendInviteDtoSchema>;

export const TransferOwnershipRequestSchema = z.object({
  toUserId: z.uuid(),
});
export type TransferOwnershipRequest = z.infer<typeof TransferOwnershipRequestSchema>;

export const RenameHouseholdRequestSchema = z.object({
  // Same `trim()` before `min(1)` as household creation: a whitespace-only rename is rejected
  // rather than stored (FR-021).
  name: z.string().trim().min(1).max(100),
});
export type RenameHouseholdRequest = z.infer<typeof RenameHouseholdRequestSchema>;

/**
 * `DELETE /api/households/:householdId` (FR-023).
 *
 * The typed-name confirmation is checked on the SERVER. A gate enforced only by a disabled button
 * is not a gate, and this is the one irreversible operation in the product.
 */
export const DeleteHouseholdRequestSchema = z.object({
  confirmName: z.string().min(1).max(100),
});
export type DeleteHouseholdRequest = z.infer<typeof DeleteHouseholdRequestSchema>;

/**
 * `PUT /api/me/active-household` (FR-032).
 *
 * The one endpoint that takes a household id from the client, and the reason FR-033 is worded as it
 * is: this is a PREFERENCE WRITE against the caller's own row, not a scope assertion. The use case
 * refuses unless the id is among the caller's active memberships, so the id is validated rather than
 * trusted. Every other endpoint resolves the household server-side.
 */
export const SetActiveHouseholdRequestSchema = z.object({
  householdId: z.uuid(),
});
export type SetActiveHouseholdRequest = z.infer<typeof SetActiveHouseholdRequestSchema>;

// --- requests --------------------------------------------------------------

export const RequestSignInLinkRequestSchema = z.object({
  email: z.email(),
});
export type RequestSignInLinkRequest = z.infer<typeof RequestSignInLinkRequestSchema>;

export const CreateHouseholdRequestSchema = z.object({
  // `trim()` runs before `min(1)`, so a whitespace-only name is rejected rather than stored
  // (FR-018, User Story 3 scenario 5).
  name: z.string().trim().min(1).max(100),
});
export type CreateHouseholdRequest = z.infer<typeof CreateHouseholdRequestSchema>;

export const CreateInviteRequestSchema = z.object({
  email: z.email(),
});
export type CreateInviteRequest = z.infer<typeof CreateInviteRequestSchema>;

export const AcceptInviteRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(100).optional(),
});
export type AcceptInviteRequest = z.infer<typeof AcceptInviteRequestSchema>;

export const UpdateProfileRequestSchema = z.object({
  displayName: z.string().trim().min(1).max(100),
});
export type UpdateProfileRequest = z.infer<typeof UpdateProfileRequestSchema>;

/**
 * `PUT /api/me/avatar`.
 *
 * Base64, no `data:` prefix. NOT multipart: a 256x256 WebP is 8-15 kB and base64 costs a third more,
 * which sits far inside Fastify's default 1 MB body limit — reaching for `@fastify/multipart` would
 * add a dependency and a second body-parsing path to carry fifteen kilobytes.
 *
 * THE BOUND HERE IS A BACKSTOP, NOT THE SIZE RULE, and the difference is a member-visible one.
 *
 * The real limit is 64 kB of decoded bytes and it belongs to `AvatarImage.create`, which refuses
 * with `IDENT_AVATAR_TOO_LARGE` — a named 422 the interface can explain. A tight bound here would
 * pre-empt that: base64 costs a third more, so a ceiling near 87 kB of string means anything
 * meaningfully oversized fails Zod first and comes back as a bare 400 with no code, while something
 * only slightly oversized gets the good error. Same mistake by the member, two different answers.
 *
 * So this is set at roughly Fastify's own 1 MB body limit: absurd payloads are still refused before
 * anything is decoded, and every plausible one reaches the rule that can name itself.
 */
export const SetAvatarRequestSchema = z.object({
  image: z.base64().max(1_400_000),
});
export type SetAvatarRequest = z.infer<typeof SetAvatarRequestSchema>;

export const RequestEmailChangeRequestSchema = z.object({
  email: z.email(),
});
export type RequestEmailChangeRequest = z.infer<typeof RequestEmailChangeRequestSchema>;

// --- errors ----------------------------------------------------------------

export const ApiErrorDtoSchema = z.object({
  code: z.string(),
  message: z.string(),
  details: z.record(z.string(), z.unknown()).optional(),
});
export type ApiErrorDto = z.infer<typeof ApiErrorDtoSchema>;
